import { useRef } from "react";
import { GripVertical, Trash2, Plus } from "lucide-react";

const inputCls = "w-full border border-ptf-primary/20 px-3 py-2 text-sm outline-none focus:border-ptf-secondary transition-colors bg-white text-ptf-text";

// Structured day-by-day itinerary editor: {title, description, meals, stay}
export default function DaysEditor({ value = [], onChange }) {
  const days = Array.isArray(value) ? value : [];
  // Stable per-row keys (not persisted to the day data itself) so React doesn't
  // mis-reuse input DOM nodes when a day is removed from the middle of the list.
  const nextIdRef = useRef(0);
  const idsRef = useRef([]);
  if (idsRef.current.length !== days.length) {
    const ids = idsRef.current.slice(0, days.length);
    while (ids.length < days.length) ids.push(`day-${nextIdRef.current++}`);
    idsRef.current = ids;
  }
  const setDay = (i, k, v) => onChange(days.map((d, idx) => (idx === i ? { ...d, [k]: v } : d)));
  const removeAt = (i) => {
    idsRef.current = idsRef.current.filter((_, idx) => idx !== i);
    onChange(days.filter((_, idx) => idx !== i));
  };
  const addDay = () => {
    idsRef.current = [...idsRef.current, `day-${nextIdRef.current++}`];
    onChange([...days, { title: "", description: "", meals: "", stay: "" }]);
  };

  return (
    <div data-testid="days-editor">
      <span className="text-[0.65rem] uppercase tracking-[0.14em] text-ptf-text/60">Day-by-day itinerary</span>
      <div className="mt-1 space-y-3">
        {days.map((d, i) => (
          <div key={idsRef.current[i] ?? i} data-testid="days-editor-item" className="border border-ptf-primary/15 bg-ptf-primary/[0.03] p-4">
            <div className="flex items-center justify-between mb-3">
              <span className="inline-flex items-center gap-2 font-display text-sm text-ptf-primary">
                <GripVertical className="h-4 w-4 text-ptf-primary/30" /> Day {i + 1}
              </span>
              <button type="button" onClick={() => removeAt(i)} data-testid="day-remove" className="grid h-7 w-7 place-items-center border border-ptf-primary/15 hover:bg-red-50">
                <Trash2 className="h-3.5 w-3.5 text-red-600" />
              </button>
            </div>
            <div className="space-y-2">
              <input value={d.title} onChange={(e) => setDay(i, "title", e.target.value)} placeholder="Day title (e.g. Arusha / Tarangire National Park) *" data-testid="day-title" className={inputCls} />
              <textarea rows={3} value={d.description || ""} onChange={(e) => setDay(i, "description", e.target.value)} placeholder="What happens this day" data-testid="day-description" className={`${inputCls} resize-none`} />
              <div className="grid grid-cols-2 gap-2">
                <input value={d.meals || ""} onChange={(e) => setDay(i, "meals", e.target.value)} placeholder="Meals (e.g. Breakfast, Lunch, Dinner)" data-testid="day-meals" className={inputCls} />
                <input value={d.stay || ""} onChange={(e) => setDay(i, "stay", e.target.value)} placeholder="Accommodation (e.g. Village homestay)" data-testid="day-stay" className={inputCls} />
              </div>
            </div>
          </div>
        ))}
        <button
          type="button"
          onClick={addDay}
          data-testid="day-add"
          className="inline-flex items-center gap-2 border border-dashed border-ptf-primary/30 px-4 py-2.5 text-xs uppercase tracking-[0.12em] text-ptf-secondary hover:bg-ptf-primary/5 transition-colors"
        >
          <Plus className="h-4 w-4" /> Add Day {days.length + 1}
        </button>
      </div>
    </div>
  );
}
